import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Space,
  Tag,
  Switch,
  message,
  Upload,
  Progress,
  Select,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import TableWithExport from "../../../generic components/TableWithExport/TableWithExport";
import { uploadOne } from "../../../utils/utils";

const rawFile = (f) => (f?.originFileObj ? f.originFileObj : f);

export const AdminTeam = () => {
  const [rows, setRows] = useState([]);
  const [deptRows, setDeptRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [addDeptModalOpen, setAddDeptModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [formDept] = Form.useForm();

  // image upload (save-time)
  const [fileList, setFileList] = useState([]); // [ {uid,name,url|originFileObj,status} ]
  const [imgPct, setImgPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ open: false, src: "", title: "" });
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptFilter, setDeptFilter] = useState(null);

  const loadDeptOptions = async () => {
    try {
      const { data } = await axios.get(routes.teamDeptAdminList, {
        params: { page: 1, limit: 1000 },
      });
      const opts = (data?.rows || [])
        .filter((d) => d.isActive)
        .sort((a, b) => a.order - b.order);
      setDeptOptions(opts.map((d) => ({ value: d._id, label: d.name })));
    } catch {}
  };
  useEffect(() => {
    loadDeptOptions();
  }, [modalOpen]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = { q, page: 1, limit: 1000 };
      if (deptFilter) params.departmentId = deptFilter;
      const { data } = await axios.get(routes.teamAdminList, { params });
      // ...same sorting as before

      setRows(data || []);
    } catch {
      message.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchList();
  }, [deptFilter]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter((r) =>
      [r.name, r.role, r.department, r.email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const openAdd = () => {
    setEditing(null);
    setImgPct(0);
    setFileList([]);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    setImgPct(0);
    setFileList(
      rec.image
        ? [
            {
              uid: "img-1",
              name: rec.image.split("/").pop() || "image",
              status: "done",
              url: rec.image,
            },
          ]
        : []
    );
    form.setFieldsValue({
      name: rec.name || "",
      role: rec.role || "",
      department: rec.department || "",
      email: rec.email || "",
      phone: rec.phone || "",
      departmentId: rows.find((r) => r._id === rec._id)?.departmentId || null, // or rec.departmentId
      department: rec.department || "",
      image: rec.image || "",
      isActive: rec.isActive !== false,
      order: rec.order ?? null,
    });
    setModalOpen(true);
  };

  const save = async (values) => {
    try {
      setSaving(true);
      setImgPct(0);

      // handle image
      let imageUrl = (values.image || "").trim();
      if (fileList[0]?.originFileObj) {
        imageUrl = await uploadOne(rawFile(fileList[0]), setImgPct);
        setImgPct(100);
      } else if (!imageUrl && fileList[0]?.url) {
        imageUrl = fileList[0].url;
      }

      const selected = deptOptions.find((o) => o.value === values.departmentId);
      const payload = {
        name: values.name,
        role: values.role || "",
        email: values.email || "",
        phone: values.phone || "",
        image: imageUrl || "",
        isActive: !!values.isActive,
        order: values.order ?? null,

        departmentId: values.departmentId || null,
        department: selected?.label || "",
      };

      if (editing?._id) {
        await axios.patch(routes.teamAdminUpdate(editing._id), payload);
        message.success("Member updated");
      } else {
        await axios.post(routes.teamAdminCreate, payload);
        message.success("Member added");
      }

      setModalOpen(false);
      fetchList();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(routes.teamAdminDelete(id));
      fetchList();
    } catch {
      message.error("Delete failed");
    }
  };

  const columns = [
    {
      title: "#",
      width: 60,
      align: "center",
      render: (_v, _r, i) => i + 1,
    },
    {
      title: "Member",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src={r.image || "https://via.placeholder.com/40x40?text=—"}
            alt=""
            style={{
              width: 36,
              height: 36,
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
          <div>
            <div style={{ fontWeight: 600 }}>{r.name}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{r.role}</div>
          </div>
        </div>
      ),
    },
    { title: "Department", dataIndex: "department", width: 160 },
    { title: "Email", dataIndex: "email", width: 220 },
    { title: "Phone", dataIndex: "phone", width: 140 },
    {
      title: "Active",
      width: 100,
      render: (_, r) =>
        r.isActive ? <Tag color="green">Active</Tag> : <Tag>Hidden</Tag>,
    },
    {
      title: "Order",
      width: 80,
      render: (_, r) => (r.order > 0 ? r.order : "—"),
    },
    {
      title: "Actions",
      width: 160,
      fixed: "right",
      render: (_, r) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
          <Button
            danger
            onClick={() => {
              Modal.confirm({
                title: "Delete member",
                content:
                  "Are you sure you want to delete this team member? This action cannot be undone.",
                okText: "Delete",
                okType: "danger",
                cancelText: "Cancel",
                onOk: async () => {
                  await remove(r._id);
                },
              });
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const loadDept = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(routes.teamDeptAdminList, {
        params: { q, page: 1, limit: 1000 },
      });
      setDeptRows(data?.rows || []);
    } catch (e) {
      message.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadDept();
  }, [addDeptModalOpen]);

  const removeDepartment = async (id) => {
    try {
      await axios.delete(routes.teamDeptAdminDelete(id));
      loadDept();
    } catch {
      message.error("Delete failed");
    }
  };
  const saveDepartments = async (v) => {
    try {
      if (editing?._id) {
        await axios.patch(routes.teamDeptAdminUpdate(editing._id), v);
      } else {
        await axios.post(routes.teamDeptAdminCreate, v);
      }
      message.success("Saved");
      formDept.resetFields();

      loadDept();
    } catch (e) {
      message.error("Save failed");
    }
  };

  const openEditDepartnemt = (r) => {
    setEditing(r);
    formDept.setFieldsValue({
      name: r.name || "",
      slug: r.slug || "",
      order: r.order ?? null,
      isActive: r.isActive !== false,
    });
    setAddDeptModalOpen(true);
  };

  const openAddDepartnemt = () => {
    setEditing(null);
    formDept.resetFields();
    setAddDeptModalOpen(true);
  };

  const deptColumns = [
    { title: "#", width: 64, align: "center", render: (_v, _r, i) => i + 1 },
    { title: "Name", dataIndex: "name" },
    { title: "Slug", dataIndex: "slug" },
    { title: "Order", dataIndex: "order", width: 80 },
    {
      title: "Active",
      width: 100,
      render: (_, r) => (r.isActive ? "Yes" : "No"),
    },
    {
      title: "Actions",
      width: 180,
      render: (_, r) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditDepartnemt(r)}>
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeDepartment(r._id)}
          />
        </Space>
      ),
    },
  ];

  const filteredDepartments = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return deptRows;
    return deptRows.filter((r) =>
      [r.name, r.slug]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [deptRows, q]);

  return (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          allowClear
          placeholder="Filter by department"
          style={{ width: 240 }}
          options={deptOptions}
          value={deptFilter}
          onChange={setDeptFilter}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openAddDepartnemt}
        >
          Add Department
        </Button>
      </Space>

      <TableWithExport
        columns={columns}
        data={filtered}
        loading={loading}
        tableName="member"
        enableReorder
        disableExport={true}
        onReorder={async (ids) => {
          await axios.patch(routes.teamAdminReorder, { ids });
          await fetchList();
        }}
        addRow={openAdd}
        getRowKey={(r) => r._id}
      />

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText={editing ? "Save" : "Create"}
        width={720}
        destroyOnClose
        confirmLoading={saving}
        title={editing ? `Edit: ${editing?.name}` : "Add Member"}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={save}
          initialValues={{
            name: "",
            role: "",
            department: "",
            email: "",
            phone: "",
            image: "",
            isActive: true,
            order: null,
          }}
        >
          <Form.Item
            label="Full Name"
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="Member name" />
          </Form.Item>

          <Space size="large" wrap>
            <Form.Item label="Role" name="role">
              <Input placeholder="e.g. Project Manager" />
            </Form.Item>
            <Form.Item
              label="Department"
              name="departmentId"
              rules={[{ required: true, message: "Select a department" }]}
            >
              <Select
                placeholder="Select department"
                options={deptOptions}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Space>

          <Space size="large" wrap>
            <Form.Item
              label="Email"
              name="email"
              rules={[{ type: "email", message: "Invalid email" }]}
            >
              <Input placeholder="email@domain.com" />
            </Form.Item>
            <Form.Item label="Phone" name="phone">
              <Input placeholder="+20 100 000 0000" />
            </Form.Item>
          </Space>

          {/* Image (upload on Save) */}
          <Form.Item label="Profile Image">
            <Space align="start" wrap style={{ gap: 12 }}>
              <Upload
                listType="picture-card"
                fileList={fileList}
                beforeUpload={() => false}
                onChange={({ fileList }) => setFileList(fileList.slice(-1))}
                onPreview={(file) =>
                  setPreview({
                    open: true,
                    src: file.url || file.thumbUrl,
                    title: file.name || "Preview",
                  })
                }
                maxCount={1}
                accept="image/*"
              >
                {fileList.length >= 1 ? null : (
                  <div style={{ width: 140 }}>
                    <UploadOutlined />{" "}
                    <div style={{ marginTop: 4 }}>Upload</div>
                  </div>
                )}
              </Upload>

              {/* show progress only when uploading a new file during save */}
              {saving && fileList[0]?.originFileObj && (
                <div style={{ width: 180, alignSelf: "center" }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Uploading…</div>
                  <Progress percent={imgPct} size="small" />
                </div>
              )}
            </Space>
          </Form.Item>

          <Space size="large" wrap>
            <Form.Item
              label="Active (visible on site)"
              name="isActive"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        open={preview.open}
        title={preview.title}
        footer={null}
        onCancel={() => setPreview((p) => ({ ...p, open: false }))}
      >
        <img alt="preview" style={{ width: "100%" }} src={preview.src} />
      </Modal>

      {/* Deptartment Modal */}
      <Modal
        open={addDeptModalOpen}
        width={720}
        footer={
          <Button type="primary" onClick={() => setAddDeptModalOpen(false)}>
            Done
          </Button>
        }
        title={editing ? "Edit Department" : "Add Department"}
      >
        <Form
          form={formDept}
          layout="vertical"
          onFinish={saveDepartments}
          initialValues={{ name: "", slug: "", isActive: true, order: null }}
        >
          <Space>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="e.g., Capacity Building" />
            </Form.Item>

            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => formDept.submit()}
            >
              Add
            </Button>
          </Space>
        </Form>
        <Table
          rowKey={(r) => r._id}
          columns={deptColumns}
          dataSource={filteredDepartments}
          loading={loading}
          pagination={false}
          size="small"
        />
      </Modal>
    </>
  );
};

export default AdminTeam;
